import { axiosGet } from '@/api/handlers';
import { useQuery } from '@/api/hooks';
import { OrganizationsModel } from '@/features/tenant-manager/models';
import { dateTimeAtRequestFormat } from '@/utils';
import type { z } from 'zod';
import { queryKeyMap } from '../review-sheet/utils/query-keys';

type OrganizationsParams = {
  historySavedAt?: string | null;
  referenceFiscalYear?: string;
  showMigrated?: true;
};

/**
 * Obtain the following organizational information associated with the project
 * organizationId
 * relatedOrganizationIds
 * otherResponsibleOrganizationIds
 * additionalJurisdictionalUsers
 */
export const useProjectHasRelatedOrganizations = <
  T = z.infer<typeof OrganizationsModel>,
>(props?: {
  projectId: string;
  params?: OrganizationsParams;
  select?: (data: z.infer<typeof OrganizationsModel>) => T;
  enabled?: boolean;
}) => {
  const { select, enabled = true, projectId, params: _params } = props ?? {};
  const { historySavedAt, showMigrated, ...resetParams } = _params ?? {};

  const params = {
    ...resetParams,
    datetimeAt: dateTimeAtRequestFormat(historySavedAt ?? undefined),
    ...(!showMigrated && { isMigrated: false }),
  };
  return useQuery({
    queryKey: queryKeyMap.projectOrganizations(projectId, params),
    queryFn: async () => {
      const organizations = await axiosGet(
        `/api/projects/${projectId}/organizations/`,
        params,
      );
      return OrganizationsModel.parse(organizations);
    },
    select,
    enabled,
    cacheTime: 60 * 60 * 12 * 1000,
    staleTime: 60 * 60 * 12 * 1000,
  });
};
